import { Component, Inject, output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { take } from 'rxjs';
import { AppService } from '../../../app.service';
import { BuyerUploadRequired } from '../../../models/auction';
import { Property } from '../../../models/property';
import { VaultDocument } from '../../../models/vaultDocument';
import { AuctionService } from '../../../services/AuctionService';
import { PropertyService } from '../../../services/PropertyService';

@Component({ standalone: false,
  selector: 'private-auction-authorization-dialog',
  templateUrl: 'private-auction-authorization-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['private-auction-authorization-dialog.component.scss']
})
export class PrivateAuctionAuthorizationDialogComponent {
  public title: string= "Register To Bid on Private Auction";
  public message: string = "An administrator will review your request and you will be notified via email. Requests remain in 'pending' status until approved or denied.";
  public confirmButtonText = "Register";
  public cancelButtonText = "Cancel";

  readonly statusChanged = output<number>();
  readonly missingBuyerUploadDocumentsChanged = output<BuyerUploadRequired[]>();

  public busy:boolean = false;

  public property!:Property;
  public missingBuyerUploadDocuments?: BuyerUploadRequired[];
  public userIsPrivateAuctionAuthorizedStatus?:number;

  public selectedFiles:{documentTypeId:number, file:File}[] = [];
  public existingVaultDocuments:{documentTypeId:number, vdArray:VaultDocument[]}[] = [];

  constructor(@Inject(MAT_DIALOG_DATA) private data: any, private dialogRef: MatDialogRef<PrivateAuctionAuthorizationDialogComponent>, private auctionService: AuctionService, private propertyService:PropertyService, private appService: AppService, private snackBar: MatSnackBar) {
    if(data){
        this.property = data.property;
        this.missingBuyerUploadDocuments = data.missingBuyerUploadDocuments;
        this.userIsPrivateAuctionAuthorizedStatus = data.userIsPrivateAuctionAuthorizedStatus;
        if(this.userApprovalPendingOrApproved) this.cancelButtonText="Close";
        this.property.auction!.buyerUploadsRequired?.forEach(bur => {
          this.existingVaultDocuments.push({documentTypeId: bur.documentTypeId!, vdArray: []});
        });
    }
  }

  ngOnInit(){
    //need to create a subcomponent which allows for uploading of requested buyer documents
    //this subcomponent will also be used in case a user decides in a latter stepp to upload the requested documents
    //this dialog can be finished without uploading the documents, but the user will not be able to bid on the auction until the admin approves the user

    //load exisitng documents for buyer
    this.propertyService.getVaultDocuments(undefined, 'auction', this.property.auction!.id, undefined, this.appService.currentUser!.id ,true).pipe(take(1)).subscribe(r=>{
      r.forEach(vd => {
        this.existingVaultDocuments.find(vdObj => vdObj.documentTypeId === vd.documentTypeId)?.vdArray.push(vd);
      });
    });
  }

  get buyerUploadsRequired(){
    return this.property.auction?.buyerUploadsRequired && this.property.auction.buyerUploadsRequired.length>0;
  }

  get userApprovalPendingOrApproved(){
    return this.userIsPrivateAuctionAuthorizedStatus === 1 || this.userIsPrivateAuctionAuthorizedStatus === 2;
  }

  public existingVaultDocumentForDocumentType(documentTypeId:number):VaultDocument[] {
    return this.existingVaultDocuments.find(vd => vd.documentTypeId === documentTypeId)?.vdArray ?? [];
  }

  public onUploadDocumentClick(bur:BuyerUploadRequired){
    console.log(bur);
  }

  public fileBrowseHandler($event:any, documentTypeId:number) {
    let index = this.selectedFiles.findIndex(f => f.documentTypeId === documentTypeId);
    if(index >= 0){
      this.selectedFiles[index].file = $event.target.files[0];
    }
    else {
      this.selectedFiles.push({documentTypeId: documentTypeId, file: $event.target.files[0]});
    }
  }

  public hasFileSelected(documentTypeId:number):boolean {
    return this.selectedFiles.some(f => f.documentTypeId === documentTypeId);
  }

  public selectedFile(documentTypeId:number) {
    let index = this.selectedFiles.findIndex(f => f.documentTypeId === documentTypeId);
    return this.selectedFiles[index].file;
  }

  public uploadSelectedFile(documentTypeId:number){
    this.busy = true;
    let index = this.selectedFiles.findIndex(f => f.documentTypeId === documentTypeId);
    let documentType = this.property.auction!.buyerUploadsRequired!.find(bur => bur.documentTypeId === documentTypeId);
    let description = documentType ? documentType.documentTypeName : "Buyer Upload Document";
    description += " for Auction " + this.property.formattedAddress;
    //create vault document and upload file to vault
    if(index >= 0 && this.selectedFiles[index].file){
      let vd=new VaultDocument();
      vd.id=0;
      vd.file=this.selectedFiles[index].file;
      vd.filename= this.selectedFiles[index].file.name;
      vd.description=description;
      vd.documentTypeId=documentTypeId;
      vd.visibility = 0; //not public: restricted to the buyer (restrictedToUserId)
      vd.context='auction'
      vd.contextId=this.property.auction!.id;
      //vd.subContextId=this.property.auction.scheduleSeqNo; //for now we don't use that
      vd.restrictedToUserId=this.appService.currentUser!.id;
      vd.contentType = this.selectedFiles[index].file.type;
      this.propertyService.storeVaultDocument(vd).pipe(take(1)).subscribe(r=>{
        if(r.success){
          this.snackBar.open('Document upload successful', '×', {
            verticalPosition: 'top',
            duration: 5000 
          });
          this.selectedFiles.splice(index, 1);
          const filtered_evd = this.existingVaultDocuments.find(vdObj => vdObj.documentTypeId === vd.documentTypeId)!;
          const vdArray = filtered_evd.vdArray;
          vdArray.push(VaultDocument.mapFromDTO(r.vaultDocument!));
          filtered_evd.vdArray = vdArray.slice(); //force change detection
          this.missingBuyerUploadDocuments = this.missingBuyerUploadDocuments?.filter(bur => bur.documentTypeId !== documentTypeId);
          this.missingBuyerUploadDocumentsChanged.emit(this.missingBuyerUploadDocuments ?? []);
          this.auctionService.verifyPrivateAuctionAuthorization(this.appService.currentUser!.id, this.property.auction!.id, this.property.id).pipe(take(1)).subscribe(r => {
            //could be more snackbar messages here, but for now we just close the dialog
          });
        }
        else{
          this.snackBar.open('Document upload failed: ' + r.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
        }
        this.busy = false;
      });
    }
  }

  public canConfirm(){
    return true;
  }

  public onConfirmClick(): void {
    this.busy = true;
    this.auctionService.requestPrivateAuctionAuthorization(this.appService.currentUser!.id, this.property.auction!.id, this.property.id).pipe(take(1)).subscribe(r => {
      if(r.success){
        this.snackBar.open('Request has been successfully submitted', '×', { verticalPosition: 'top', duration: 5000 });
        this.userIsPrivateAuctionAuthorizedStatus = 1; //set status to pending
        this.statusChanged.emit(this.userIsPrivateAuctionAuthorizedStatus);
        if(!this.buyerUploadsRequired) this.dialogRef.close(true); //in case the user should upload docs don't close dialog, user can still upload documents
      }
      else{
        this.snackBar.open('Sending request failed. Please try agin later or contact us: ' + r.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
      }
      this.busy = false;
    });
  }

  public onCancelClick(): void {
    this.dialogRef.close(false);
  }

}